import { defineStore } from "pinia";
import { WSClient } from "@/ems/WSClient";
import { Serie, Cat, Variable, Filter, FilterGroupLists, SerieExtraIdentityVars } from "@/types/serie";
import { getUserPermission, Role, checkUserHasPermission } from "@/ems/perms";
import { handleCurrentSerieExtra } from "@/ems/format_extra";
import { DescriptiveSummary } from "@/views/series/descriptive/types";
import { ref, Ref, reactive, watch, computed } from "vue";
import { buildParentChildObjects, parse_from_storage } from "@/ems/utils";
import router from "@/router";

interface CatHeritageObject {
    parents?: Record<number | string, (number | string)[]>;
    children?: Record<number | string, (number | string)[]>;
}

type StringOrNumberOrNull = string | number | null;

const waitForLoading = (timeoutMs: number, loading_id: Ref<StringOrNumberOrNull>): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        let timeoutId: number = 0;
        const stopWatch = watch(
            loading_id,
            (loading_id) => {
                if (!loading_id) {
                    clearTimeout(timeoutId);
                    stopWatch();
                    resolve();
                }
            },
            { immediate: false }
        );
        timeoutId = setTimeout(() => {
            stopWatch();
            reject(new Error(`Timeout waiting for Serie to load `));
        }, timeoutMs) as any as number;
    });
};

const LOADING_TIMEOUT = 10000;

export const serieContextStore = defineStore("serie_context", () => {
    const serie_user_permissions = ref<Record<string, boolean>>({});
    const serie = reactive({} as Serie);
    const serie_roles = ref<Role[]>([]);
    const descriptive = ref({} as DescriptiveSummary);
    const categories = ref<Cat[] | undefined>(undefined);
    const variablesAll = ref<Variable[] | undefined>(undefined);
    const variables = computed(() => {
        const identityVars = serie.Extra?.identity_variables;
        return filterOutUncollectedVars(variablesAll.value ?? [], identityVars) ?? [];
    });
    const has_custom_var = ref(false);
    const filters = ref<Filter[] | undefined>((parse_from_storage(sessionStorage, "filters") ?? []) as Filter[]);
    const filter_history = ref<Filter[]>((parse_from_storage(sessionStorage, "filter_history") ?? []) as Filter[]);
    const filter_groups = ref<FilterGroupLists>((parse_from_storage(sessionStorage, "filter_groups") ?? {}) as FilterGroupLists);
    const serie_filtred_site_var_id = ref<string | number>();
    const serie_filtred_site_value = ref<string | undefined>();
    const shared_categorie_ids = ref<number[] | undefined>([]);
    const set_of_visible_vars = reactive(new Set<number | string>());

    const cat_heritage_object = ref<CatHeritageObject>({});

    /// Loading indicatiors
    const loading_serie_id = ref<StringOrNumberOrNull>(null);
    const loading_vars_serie_id = ref<StringOrNumberOrNull>(null);
    const loading_cats_serie_id = ref<StringOrNumberOrNull>(null);
    const loading_permissions_serie_id = ref<StringOrNumberOrNull>(null);

    const loading_cats_or_vars = computed(() => {
        return loading_vars_serie_id.value !== null || loading_cats_serie_id.value !== null || loading_serie_id.value !== null;
    });

    // loaded serie_id indicators
    const loaded_vars_serie_id = ref<StringOrNumberOrNull>(null);
    const loaded_cats_serie_id = ref<StringOrNumberOrNull>(null);
    const loaded_permissions_serie_id = ref<StringOrNumberOrNull>(null);

    const can_randomize = ref(false);
    const can_view_randomization = ref(false);

    const filterOutUncollectedVars = (vars: Variable[], newIdentVars: SerieExtraIdentityVars) => {
        if (!newIdentVars) {
            return vars;
        }
        const filtered_vars:string[] = [];
        if (!newIdentVars.collect_birthdate) {
            filtered_vars.push("BirthDate","Age","AgeAtInclusion","AgeAtDeath","AgeAtLastFU");
        }
        if (!newIdentVars.collect_gender) {
            filtered_vars.push("Gender");
        }
        if (!newIdentVars.collect_death_date) {
            filtered_vars.push("DeathDate","AgeAtDeath");
        }
        if (!newIdentVars.collect_inclusion_last_follow_up) {
            filtered_vars.push("AgeAtLastFU","FollowUp","LastConsultation");
        }

         return vars?.filter((v)=>{
            if(Number.parseInt(v.VarId.toString()) > 0) {
                return true
            }
                return !filtered_vars.includes(v.VarId.toString());
        }) ?? [];
    }

    const get_user_permissions = async (
        client: WSClient,
        preferences: string[],
        force_query = false
    ): Promise<Record<string, boolean>> => {
        if (!serie.SerieId) throw Error("Cannot get user without serie id set");
        const missingPreferences = preferences.filter((preference) => !(preference in serie_user_permissions.value));
        if (!force_query && missingPreferences.length == 0) {
            if (loaded_permissions_serie_id.value == serie.SerieId && !serie_user_permissions.value) {
                return Promise.resolve(serie_user_permissions.value);
            } else if (loading_permissions_serie_id.value == serie.SerieId) {
                await waitForLoading(LOADING_TIMEOUT, loading_permissions_serie_id);
                if (!serie_user_permissions.value) {
                    return Promise.resolve(serie_user_permissions.value);
                }
            }
        }
        if (missingPreferences.length > 0) {
            loading_permissions_serie_id.value = serie.SerieId;
            console.log("Getting permissions for serie " + missingPreferences);
            try {
                const queried_prefs = await getUserPermission(client, missingPreferences, serie.SerieId);
                Object.keys(queried_prefs).forEach((key) => {
                    serie_user_permissions.value[key] = queried_prefs[key];
                });
                loaded_permissions_serie_id.value = serie.SerieId;
            } finally {
                loading_permissions_serie_id.value = null;
            }
        }

        const returned_permissions = Object.fromEntries(
            Object.entries(serie_user_permissions.value).filter(([key]) => preferences.includes(key))
        );
        return Promise.resolve(returned_permissions);
    };

    async function load_serie(client: WSClient, serie_id: string | number, force_query = false): Promise<Serie> {
        if (!force_query) {
            if (serie.SerieId && serie_id == serie.SerieId) {
                // Serie already loaded
                return Promise.resolve(serie);
            } else if (loading_serie_id.value && serie_id == loading_serie_id.value) {
                // Serie is loading wait for it to finish
                await waitForLoading(LOADING_TIMEOUT, loading_serie_id);
                return Promise.resolve(serie);
            }
        }
        console.log("Openning serie " + serie_id);
        loading_serie_id.value = serie_id;
        let result: {
            serie: Serie;
            roles: Role[];
            descriptive_summary: DescriptiveSummary;
            shared_categorie_ids?: number[];
        };
        try {
            result = await client.queryWs("GET", "serie_serie", {
                serie_id: serie_id,
                permissions: true,
                descriptive_summary: false,
                with_plan: true,
                roles: true,
                withProdTooltip: true,
                patient_count: true,
                randomization: true,
            });
        } catch (error) {
            loading_serie_id.value = null;
            if (client.status_code == 404) {
                router.push({ name: "SeriesList", query: { e: 1 } });
                return Promise.resolve({} as Serie);
            }
            if (client.status_code == 403) {
                router.push({ name: "NotAllowedInSerie" });
                return Promise.resolve({} as Serie);
            }
            return Promise.reject(error);
        }
        try {
            await set_user_randomization_permission(client);
        } finally {
            loading_serie_id.value = null;
        }

        if (!result.serie) throw new Error("Serie does not exists");

        result.serie = handleCurrentSerieExtra(result.serie);
        descriptive.value = result.descriptive_summary;
        Object.assign(serie, result.serie);
        serie_roles.value = result.roles;
        shared_categorie_ids.value = result.shared_categorie_ids;
        return serie;
    }

    async function load_filter(client: WSClient): Promise<void> {
        const resp = await client.queryWs<{
            filters: Filter[];
            filter_history: Filter[];
            filter_groups: FilterGroupLists;
            serie_filtred_site_value: string | undefined
        }>("GET", "filter_session", {
            serie_id: serie.SerieId,
            filter_history: true,
            filter_groups: true,
        });
        filters.value = resp.filters ?? [];
        filter_history.value = resp.filter_history;
        serie_filtred_site_value.value = resp.serie_filtred_site_value;
        Object.assign(filter_groups.value, resp.filter_groups);
    }

    async function load_cats(client: WSClient, force_query = false): Promise<Cat[]> {
        if (!force_query) {
            if (loaded_cats_serie_id.value == serie.SerieId && categories.value !== undefined) {
                return Promise.resolve(categories.value);
            } else if (loading_cats_serie_id.value == serie.SerieId) {
                await waitForLoading(LOADING_TIMEOUT, loading_cats_serie_id);
                if (categories.value !== undefined) {
                    return Promise.resolve(categories.value);
                }
            }
        }
        loading_cats_serie_id.value = serie.SerieId;
        try {
            const resp = await client.queryWs<{ variables_categories: Cat[] }>("GET", "variable_varCatOfSerie", {
                serie_id: serie.SerieId,
            });
            categories.value = resp.variables_categories;
            loaded_cats_serie_id.value = serie.SerieId;
            return Promise.resolve(categories.value);
        } finally {
            loading_cats_serie_id.value = null;
        }
    }

    async function load_vars(client: WSClient, force_query = false): Promise<Variable[]> {
        if (!force_query) {
            if (loaded_vars_serie_id.value == serie.SerieId && variablesAll.value !== undefined) {
                return Promise.resolve(variables.value);
            } else if (loading_vars_serie_id.value == serie.SerieId) {
                await waitForLoading(LOADING_TIMEOUT, loading_vars_serie_id);
                if (variablesAll.value !== undefined) {
                    return Promise.resolve(variables.value);
                }
            }
        }
        loading_vars_serie_id.value = serie.SerieId;
        try {
            const resp = await client.queryWs<{ variables: Variable[], has_custom_var: boolean }>("GET", "variable_variable", {
                var_count: is_var_count.value,
                category: true,
                with_not_analysed: true,
                with_picture_exists: true,
                all_data: true,
                sid: serie.SerieId,
            });
            variablesAll.value = resp.variables;
            has_custom_var.value = resp.has_custom_var;
            loaded_vars_serie_id.value = serie.SerieId;
            return Promise.resolve(variables.value);
        } finally {
            loading_vars_serie_id.value = null;
        }
    }

    function mergeVariable(variable: Variable): void {
        const current = variablesAll.value ?? [];
        const idx = current.findIndex((v) => v.VarId === variable.VarId);
        let updated: Variable[];
        if (idx >= 0) {
            updated = [...current];
            updated[idx] = variable;
        } else {
            updated = [...current, variable];
        }
        variablesAll.value = updated;
    }
    function deleteVariable(VarId: number | string): void {
      const current = variablesAll.value ?? [];
      const idx = current.findIndex((v) => v.VarId == VarId);
      if (idx >= 0) {
        current.splice(idx, 1);
      }
      variablesAll.value = current;
    }

    const site_var = computed(() => {
        return variablesAll.value?.find((v) => v.Role == "SITE");
    });
    const randomization_var = computed(() => {
        return variablesAll.value?.find((v) => v.Role == "RANDOMIZATION");
    });
    const is_var_count = computed(() => {
        return serie.NumberOfPatients > 0 && serie.NumberOfPatients < 5000;
    });
    const check_filter_site = function () {
        if (filters.value === undefined || filters.value.length === 0) return;
        console.log("filters:", filters.value);
        const first_site_filter_vars = filters.value.filter((aVar: Filter) => aVar?.variable?.Role === "SITE").shift();
        if (first_site_filter_vars?.variable?.VarId !== undefined) {
            serie_filtred_site_var_id.value = first_site_filter_vars.variable.VarId;
        }
    };
    const recompute_cat_heritage_object = function () {
        cat_heritage_object.value = buildParentChildObjects(categories.value ?? []);
    };

    async function set_user_randomization_permission(client: WSClient) {
        const [randomization_permission, randomization_view_arm_permission] = await Promise.all([
            checkUserHasPermission("serie-randomization"),
            checkUserHasPermission("serie-randomization-view-arm"),
        ]);
        can_randomize.value = randomization_permission;
        can_view_randomization.value = randomization_view_arm_permission;
    }

    watch(filters, (newValue) => {
        sessionStorage.setItem("filters", JSON.stringify(newValue));
    });

    watch(filter_history, (newValue) => {
        sessionStorage.setItem("filter_history", JSON.stringify(newValue));
    });

    watch(filter_groups, (newValue) => {
        sessionStorage.setItem("filter_groups", JSON.stringify(newValue));
    });

    return {
        serie_user_permissions,
        serie,
        serie_roles,
        descriptive,
        categories,
        cat_heritage_object,
        recompute_cat_heritage_object,
        variables,
        has_custom_var,
        site_var,
        randomization_var,
        filters,
        filter_history,
        filter_groups,
        check_filter_site,
        serie_filtred_site_var_id,
        serie_filtred_site_value,
        shared_categorie_ids,
        set_of_visible_vars,
        get_user_permissions,
        load_serie,
        load_filter,
        load_cats,
        load_vars,
        mergeVariable,
        deleteVariable,
        is_var_count,
        loading_cats_or_vars,
        can_randomize,
        can_view_randomization,
        set_user_randomization_permission,
    };
});
